import Vue from 'vue';
import LoadScript from 'vue-plugin-load-script';
import apiService from '@/services/api.service';
import store from '@/store/index';
import utils from '@/utils/index';
import axios from 'axios';

const axiosService = axios.create({
  headers: {
    'Content-Type': 'application/json'
  }
});

const apiURL = process.env.NODE_ENV === 'production' ? '/api/' : process.env.VUE_APP_API_URL;
const createHeaders = (jwt) => ({ headers: { Authorization: `Bearer ${jwt}` } });

Vue.use(LoadScript);

const localUser = {
  name: 'Local User',
  email: 'local.user@darkwolfsolutions.com',
  role: 'Viewer',
  groups: ['VIEW_ONLY']
};

const getGapi = () => Vue.loadScript('https://apis.google.com/js/platform.js?onload=init')
  .then(() => (
    // Script is loaded, do something
    window.gapi
  ))
  .catch(() => {
    // Failed to fetch script
    throw Error('Failed to load library');
  });

let instance;
export const getInstance = () => instance;

export const googleAPI = ({
  ...config
}) => {
  if (instance) return instance;
  instance = new Vue({
    data() {
      return {
        user: {
          name: '',
          email: '',
          role: '',
          groups: []
        },
        gapi: undefined,
        loading: true,
        isSignedIn: false,
        googleSignedIn: false,
        error: null
      };
    },
    async created() {
      await this.init();
    },
    watch: {
      error(message) {
        if (message !== null) store.commit('throwAuthError', message);
      }
    },
    methods: {
      async init() {
        if (utils.bypassAuth()) {
          this.loading = false;
          this.isSignedIn = true;
          this.googleSignedIn = true;
          this.user = localUser;
          try {
            const data = await apiService.get('profile/');
            this.user.role = data.results[0].role;
            this.user.groups = data.results[0].user.groups.map((group) => group.name);
            store.commit('setGroups', this.user.groups);
          } catch {
            this.error = 'Unable to retrieve profile.';
          }
          return;
        }
        try {
          this.gapi = await getGapi(config);
          await this.load();
        } catch {
          this.error = 'OAuth Configuration failed.';
        }
      },
      async load() {
        await this.gapi.load('auth2', () => {
          this.gapi.auth2.init({
              client_id: config.client_id,
              fetch_basic_profile: true,
              ux_mode: 'redirect',
              redirect_uri: `${window.location.origin}${config.redirect_uris[0]}`
            })
            .then(() => {
              this.setInitialized('GoogleAuth', this.gapi.auth2.getAuthInstance());
              if (this.gapi.auth2.GoogleAuth.isSignedIn.get()) {
                this.googleSignedIn = true;
                this.setGoogleToken();
              }
              this.loading = false;
            })
            .catch(() => {
              this.loading = false;
              this.error = 'Unable to connect to the OAuth service. Contact an administrator.';
            });
        });
      },
      setInitialized(field, value) {
        Vue.set(this.gapi.auth2, field, value);
      },
      setGoogleToken() {
        this.setInitialized('GoogleUser', this.gapi.auth2.GoogleAuth.currentUser.get());
        this.setInitialized('AuthResponse', this.gapi.auth2.GoogleUser.getAuthResponse(true));
        this.setInitialized('BasicProfile', this.gapi.auth2.GoogleUser.getBasicProfile());
        window.localStorage.setItem('playbook_token', this.gapi.auth2.AuthResponse.id_token);
      },
      async getDjangoToken() {
        const loginURL = `${apiURL.slice(0, -4)}get_token/`;
        try {
          const response = await axiosService
            .post(loginURL, {}, createHeaders(window.localStorage.getItem('playbook_token')));
          window.localStorage.setItem('app_token', response.data.token);
          this.isSignedIn = true;
          await this.setUser();
        } catch (err) {
          this.error = 'Unable to authenticate to the API. Contact an administrator.';
          this.isSignedIn = false;
          throw err;
        }
      },
      async setUser() {
        let profile;
        let groups;
        try {
          const data = await apiService.get('profile/');
          [profile] = data.results;
          groups = profile.user.groups.map((group) => group.name);
        } catch {
          this.error = 'Unable to retrieve profile.';
          profile = localUser;
          groups = localUser.groups;
        }

        this.user = {
          id: this.gapi.auth2.BasicProfile.getId(),
          name: this.gapi.auth2.BasicProfile.getName(),
          first: this.gapi.auth2.BasicProfile.getGivenName(),
          last: this.gapi.auth2.BasicProfile.getFamilyName(),
          image_url: this.gapi.auth2.BasicProfile.getImageUrl(),
          email: this.gapi.auth2.BasicProfile.getEmail(),
          role: profile.role,
          groups
        };
        store.commit('setGroups', this.user.groups);
      },
      async signIn() {
        if (utils.bypassAuth()) return;
        const options = {
          prompt: 'select_account',
          scope: 'profile email'
        };
        await this.gapi.auth2.GoogleAuth.signIn(options)
          .then(() => {
            this.setGoogleToken();
          })
          .catch(() => {
            this.error = 'Unable to login.';
          });
      },
      async signOut() {
        const logoutURL = `${apiURL.slice(0, -4)}logout/`;
        if (utils.bypassAuth()) return;
        await this.gapi.auth2.GoogleAuth.signOut()
          .then(async () => {
        window.localStorage.removeItem('playbook_token');
        window.localStorage.removeItem('agreedToPrivacy');
        this.gapi.auth2.GoogleUser.disconnect();
            this.gapi.auth2.GoogleAuth.disconnect();
            this.user = {};
            this.isSignedIn = false;
            this.googleSignedIn = false;
            await axiosService
              .post(logoutURL, {}, createHeaders(window.localStorage.getItem('app_token')));
            window.localStorage.removeItem('app_token');
          })
          .catch(() => {
            this.error = 'Error while logging out.';
          });
      }
    }
  });
  return instance;
};

export const PlaybookAuth = {
  install(Vue, config) {
    Vue.prototype.$auth = googleAPI(config);
  }
};
